import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Landmark, Loader2, CheckCircle2, AlertCircle, ExternalLink, Wallet, Clock, History, XCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { money } from "@/lib/format";

export const useConnectPayout = () => {
  const status = useQuery({ queryKey: ["enh-connect"], queryFn: () => api.get("/enhancer/connect/status").then((r) => r.data), retry: false });
  const earnings = useQuery({ queryKey: ["enh-earnings-alltime"], queryFn: () => api.get("/enhancer/me/earnings").then((r) => r.data) });
  const stripeBal = useQuery({ queryKey: ["enh-connect-balance"], queryFn: () => api.get("/enhancer/connect/balance").then((r) => r.data), retry: false, enabled: !!status.data?.connected });
  const ready = status.data?.connected && status.data?.payouts_enabled;
  const pending = status.data?.connected && !status.data?.payouts_enabled;
  const unpaid = Number(earnings.data?.pending ?? 0);
  const paid = Number(earnings.data?.paid ?? 0);
  return { status, earnings, stripeBal, ready, pending, unpaid, paid, loading: status.isLoading };
};

export const startOnboarding = async (setBusy) => {
  if (setBusy) setBusy(true);
  try {
    const returnUrl = `${window.location.origin}/dashboard?connect=done`;
    const { data: res } = await api.post("/enhancer/connect/onboard", { return_url: returnUrl, refresh_url: returnUrl });
    window.location.href = res.url;
  } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); if (setBusy) setBusy(false); }
};

function PayoutHistory() {
  const { data, isLoading } = useQuery({
    queryKey: ["enh-payout-records"],
    queryFn: () => api.get("/enhancer/me/payout-records").then((r) => r.data.records),
  });
  if (isLoading) return <div className="py-4 text-center"><Loader2 className="animate-spin inline text-[#144F75]" size={16} /></div>;
  const recs = data || [];
  if (!recs.length) return <p className="text-xs text-[#8FA89B] py-2">No payout history yet.</p>;
  return (
    <div className="space-y-2 mt-2" data-testid="enh-payout-history">
      {recs.map((r) => (
        <div key={r.id} className="flex items-center justify-between text-sm bg-[#F9F7F3] rounded-lg px-3 py-2.5">
          <div>
            <span className={`text-[10px] rounded-full px-2 py-0.5 mr-2 ${r.status === "paid" ? "bg-[#5C8A72]/15 text-[#5C8A72]" : "bg-red-100 text-red-600"}`}>
              {r.status === "paid" ? <CheckCircle2 className="inline" size={9} /> : <XCircle className="inline" size={9} />} {r.status}
            </span>
            <span className="text-[#8FA89B] text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</span>
          </div>
          <span className={`font-semibold text-sm ${r.status === "paid" ? "text-[#5C8A72]" : "text-red-500"}`}>{money(Number(r.amount || 0))}</span>
        </div>
      ))}
    </div>
  );
}

export default function ConnectPayoutCard() {
  const { status, stripeBal, ready, pending, unpaid, paid, loading } = useConnectPayout();
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const data = status.data;

  const openDashboard = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/enhancer/connect/dashboard");
      window.open(res.url, "_blank");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="eyw-card p-5 mb-6 border-l-4 border-l-[#635BFF]" data-testid="connect-payout-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Landmark size={20} className="text-[#635BFF]" /><h3 className="font-serif text-xl text-[#1A242B]">Payouts &amp; bank</h3></div>
        <button onClick={() => setShowHistory(!showHistory)} data-testid="toggle-payout-history" className="text-xs text-[#4A5D6B] flex items-center gap-1 hover:text-[#144F75]">
          <History size={13} /> {showHistory ? "Hide" : "History"}
        </button>
      </div>

      {/* Earnings-to-date summary */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="rounded-xl bg-[#F9F7F3] px-4 py-3" data-testid="payout-earned-todate">
          <p className="text-[11px] uppercase tracking-wide text-[#8FA89B] flex items-center gap-1"><Wallet size={11} /> Earned to date</p>
          <p className="font-serif text-2xl text-[#144F75]">{money((unpaid + paid))}</p>
        </div>
        <div className="rounded-xl bg-[#D98E73]/[0.1] px-4 py-3" data-testid="payout-awaiting">
          <p className="text-[11px] uppercase tracking-wide text-[#B5794E] flex items-center gap-1"><Clock size={11} /> Awaiting payout</p>
          <p className="font-serif text-2xl text-[#B5794E]">{money(unpaid)}</p>
        </div>
      </div>

      {loading ? (
        <div className="py-2"><Loader2 className="animate-spin text-[#144F75]" size={18} /></div>
      ) : ready ? (
        <>
          <p className="text-sm text-[#5C8A72] flex items-center gap-1.5 mb-3" data-testid="connect-status-ready"><CheckCircle2 size={15} /> Bank connected — you're all set to receive payouts directly.</p>
          {stripeBal.data?.available != null && (
            <p className="text-xs text-[#4A5D6B] mb-3" data-testid="connect-stripe-balance">
              In your Stripe account: <span className="font-medium text-[#5C8A72]">{money(stripeBal.data.available)} available</span>
              {stripeBal.data.pending > 0 && <> · {money(stripeBal.data.pending)} on the way</>} — paid out to your bank automatically.
            </p>
          )}
          <button onClick={openDashboard} disabled={busy} data-testid="connect-dashboard-btn" className="eyw-btn-ghost !py-2 text-sm">{busy ? <Loader2 className="animate-spin" size={14} /> : <ExternalLink size={14} />} Manage payout account</button>
        </>
      ) : pending ? (
        <>
          <p className="text-sm text-[#B5794E] flex items-center gap-1.5 mb-3" data-testid="connect-status-pending"><AlertCircle size={15} /> Almost there — Stripe still needs a few details before payouts can be enabled.</p>
          <button onClick={() => startOnboarding(setBusy)} disabled={busy} data-testid="connect-continue-btn" className="eyw-btn !py-2 text-sm">{busy ? <Loader2 className="animate-spin" size={14} /> : null} Finish setup</button>
        </>
      ) : (
        <>
          <p className="text-sm text-[#4A5D6B] mb-3" data-testid="connect-status-none">
            {unpaid > 0
              ? <>You have <span className="font-semibold text-[#B5794E]">{money(unpaid)}</span> in earnings waiting. Connect a payout account to get paid directly — setup is handled securely by Stripe.</>
              : "Connect a payout account to get paid directly for your sessions, courses and products. Setup is handled securely by Stripe and takes a couple of minutes."}
          </p>
          <button onClick={() => startOnboarding(setBusy)} disabled={busy} data-testid="connect-setup-btn" className="eyw-btn !py-2 text-sm">{busy ? <Loader2 className="animate-spin" size={14} /> : <Landmark size={14} />} Set up payouts</button>
        </>
      )}
      {data?.error && <p className="text-xs text-[#B45151] mt-2">{String(data.error).slice(0, 140)}</p>}

      {showHistory && (
        <div className="mt-4 border-t border-[#E5E0D8] pt-4">
          <p className="text-xs font-medium text-[#4A5D6B] mb-2 flex items-center gap-1"><History size={12} /> Payout history</p>
          <PayoutHistory />
        </div>
      )}
    </div>
  );
}
